import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Calendar, PieChart, BarChart3, RefreshCw } from 'lucide-react';
import { useSalonId } from '../../hooks/useSalonId';
import { financesApi, type FinancialStatsResponse } from '../../api/financesApi';

type Period = 'today' | 'week' | 'month' | 'year';

const periodLabels: Record<Period, string> = {
    today: 'Hoy',
    week: 'Esta Semana',
    month: 'Este Mes',
    year: 'Este Año',
};

const FinancesPage = () => {
    const salonId = useSalonId();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<Period>('today');
    const [financialData, setFinancialData] = useState<FinancialStatsResponse | null>(null);

    // Fetch financial data when salon or period changes
    useEffect(() => {
        if (!salonId) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                let data: FinancialStatsResponse;

                switch (selectedPeriod) {
                    case 'today':
                        data = await financesApi.getTodayStats(salonId);
                        break;
                    case 'week':
                        data = await financesApi.getWeekStats(salonId);
                        break;
                    case 'month':
                        data = await financesApi.getMonthStats(salonId);
                        break;
                    case 'year':
                        data = await financesApi.getYearStats(salonId);
                        break;
                }

                setFinancialData(data);
            } catch (err) {
                console.error('Error loading financial data:', err);
                setError('Error al cargar datos financieros');
                // Set empty data to avoid crashes
                setFinancialData({
                    totalRevenue: 0,
                    totalCommissions: 0,
                    netProfit: 0,
                    profitMargin: 0,
                    averageTicket: 0,
                    growthPercentage: 0,
                    totalAppointments: 0,
                    completedAppointments: 0,
                    topServices: [],
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [salonId, selectedPeriod]);

    const handleRefresh = () => {
        if (salonId) {
            setIsLoading(true);
            // Trigger re-fetch by toggling period
            const currentPeriod = selectedPeriod;
            setSelectedPeriod('today');
            setTimeout(() => setSelectedPeriod(currentPeriod), 10);
        }
    };

    const formatCurrency = (value: number | undefined | null) => {
        if (value == null || isNaN(value)) {
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(0);
        }
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercentage = (value: number | undefined | null) => {
        if (value == null || isNaN(value)) return '0%';
        return `${value.toFixed(1)}%`;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Finanzas</h1>
                    <p className="text-gray-500">
                        Análisis financiero - <span className="font-medium text-emerald-600">{periodLabels[selectedPeriod]}</span>
                    </p>
                </div>

                {/* Period Selector & Refresh - only show if salon exists */}
                {salonId && (
                    <div className="flex items-center gap-2 md:gap-4 self-start md:self-auto w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                            title="Refrescar datos"
                        >
                            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="flex gap-2 bg-white p-1 rounded-lg border border-gray-200 overflow-x-auto flex-nowrap min-w-min">
                            {(['today', 'week', 'month', 'year'] as Period[]).map(period => (
                                <button
                                    key={period}
                                    onClick={() => setSelectedPeriod(period)}
                                    className={`px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedPeriod === period
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    {period === 'today' && 'Hoy'}
                                    {period === 'week' && 'Semana'}
                                    {period === 'month' && 'Mes'}
                                    {period === 'year' && 'Año'}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                </div>
            )}

            {/* Empty State when no salon */}
            {!salonId ? (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                    <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Sin datos financieros</h3>
                    <p className="mt-2 text-gray-500">Primero debes configurar tu salón para ver las estadísticas financieras.</p>
                    <a href="/app/onboarding" className="mt-6 inline-block px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                        Configurar Salón
                    </a>
                </div>
            ) : (
                <>
                    {/* Financial Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Revenue */}
                        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 rounded-xl shadow-lg text-white">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium opacity-90">Ingresos Totales</h3>
                                <DollarSign className="w-5 h-5 opacity-80" />
                            </div>
                            {isLoading ? (
                                <div className="animate-pulse">
                                    <div className="h-8 bg-white/20 rounded w-3/4 mb-2"></div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-3xl font-bold mb-1">
                                        {formatCurrency(financialData?.totalRevenue)}
                                    </p>
                                    <div className="flex items-center gap-1 text-sm opacity-90">
                                        {(financialData?.growthPercentage ?? 0) >= 0 ? (
                                            <TrendingUp className="w-4 h-4" />
                                        ) : (
                                            <TrendingDown className="w-4 h-4" />
                                        )}
                                        <span>
                                            {(financialData?.growthPercentage ?? 0) >= 0 ? '+' : ''}
                                            {formatPercentage(financialData?.growthPercentage)} vs período anterior
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Total Commissions */}
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-500">Comisiones Pagadas</h3>
                                <div className="p-2 bg-red-50 rounded-lg">
                                    <TrendingDown className="w-5 h-5 text-red-600" />
                                </div>
                            </div>
                            {isLoading ? (
                                <div className="animate-pulse">
                                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">
                                        {formatCurrency(financialData?.totalCommissions)}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {financialData?.completedAppointments ?? 0} citas completadas
                                    </p>
                                </>
                            )}
                        </div>

                        {/* Net Profit */}
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-500">Utilidad Neta</h3>
                                <div className="p-2 bg-blue-50 rounded-lg">
                                    <BarChart3 className="w-5 h-5 text-blue-600" />
                                </div>
                            </div>
                            {isLoading ? (
                                <div className="animate-pulse">
                                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">
                                        {formatCurrency(financialData?.netProfit)}
                                    </p>
                                    <p className="text-xs text-emerald-600 font-medium">
                                        Margen: {formatPercentage(financialData?.profitMargin)}
                                    </p>
                                </>
                            )}
                        </div>

                        {/* Average Ticket */}
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-500">Ticket Promedio</h3>
                                <div className="p-2 bg-purple-50 rounded-lg">
                                    <Calendar className="w-5 h-5 text-purple-600" />
                                </div>
                            </div>
                            {isLoading ? (
                                <div className="animate-pulse">
                                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">
                                        {formatCurrency(financialData?.averageTicket)}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Por cita completada
                                    </p>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Charts and Details */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Top Services */}
                        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-semibold text-gray-900">Servicios Más Rentables</h3>
                                <PieChart className="w-5 h-5 text-gray-400" />
                            </div>

                            {isLoading ? (
                                <div className="space-y-4">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <div key={i} className="animate-pulse flex items-center gap-4">
                                            <div className="h-12 w-12 bg-gray-200 rounded"></div>
                                            <div className="flex-1 space-y-2">
                                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : !financialData?.topServices?.length ? (
                                <div className="text-center py-12">
                                    <PieChart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No hay datos de servicios para este período</p>
                                    <p className="text-sm text-gray-400 mt-1">Los servicios aparecerán cuando haya citas completadas</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {financialData.topServices.map((service, index) => {
                                        const totalRevenue = financialData.totalRevenue || 1;
                                        const percentage = (service.revenue / totalRevenue) * 100;
                                        return (
                                            <div key={service.id} className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-yellow-500' :
                                                            index === 1 ? 'bg-gray-400' :
                                                                index === 2 ? 'bg-orange-600' :
                                                                    'bg-emerald-500'
                                                            }`}>
                                                            {index + 1}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">{service.name}</p>
                                                            <p className="text-sm text-gray-500">{service.count} servicios</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-semibold text-gray-900">
                                                            {formatCurrency(service.revenue)}
                                                        </p>
                                                        <p className="text-sm text-gray-500">
                                                            {formatPercentage(percentage)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-2">
                                                    <div
                                                        className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Summary Stats */}
                        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                            <h3 className="font-semibold text-gray-900 mb-4">Resumen del Período</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-500">Total Citas</span>
                                    <span className="font-semibold text-gray-900">{financialData?.totalAppointments ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-500">Completadas</span>
                                    <span className="font-semibold text-emerald-600">{financialData?.completedAppointments ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                                    <span className="text-gray-500">Tasa de Éxito</span>
                                    <span className="font-semibold text-gray-900">
                                        {financialData?.totalAppointments
                                            ? formatPercentage((financialData.completedAppointments / financialData.totalAppointments) * 100)
                                            : '0%'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between py-3">
                                    <span className="text-gray-500">Servicios Top</span>
                                    <span className="font-semibold text-gray-900">{financialData?.topServices?.length ?? 0}</span>
                                </div>
                            </div>

                            {/* Quick insights */}
                            <div className="mt-6 p-4 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-lg">
                                <p className="text-sm font-medium text-emerald-700">💡 Consejo</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    {(financialData?.profitMargin ?? 0) > 50
                                        ? 'Excelente margen de utilidad. ¡Sigue así!'
                                        : (financialData?.profitMargin ?? 0) > 30
                                            ? 'Buen margen. Considera optimizar comisiones.'
                                            : 'Revisa la estructura de precios y comisiones.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Reports Section */}
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                        <h3 className="font-semibold text-gray-900 mb-4">Reportes Detallados</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <button className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-all group">
                                <DollarSign className="w-8 h-8 text-gray-400 group-hover:text-emerald-600 mx-auto mb-2" />
                                <p className="font-medium text-gray-700 group-hover:text-emerald-700">Reporte de Comisiones</p>
                                <p className="text-xs text-gray-500 mt-1">Por empleado y período</p>
                            </button>

                            <button className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group">
                                <Calendar className="w-8 h-8 text-gray-400 group-hover:text-blue-600 mx-auto mb-2" />
                                <p className="font-medium text-gray-700 group-hover:text-blue-700">Análisis Mensual</p>
                                <p className="text-xs text-gray-500 mt-1">Comparativa y proyecciones</p>
                            </button>

                            <button className="p-4 border-2 border-dashed border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group">
                                <PieChart className="w-8 h-8 text-gray-400 group-hover:text-purple-600 mx-auto mb-2" />
                                <p className="font-medium text-gray-700 group-hover:text-purple-700">Exportar Datos</p>
                                <p className="text-xs text-gray-500 mt-1">Excel, PDF, CSV</p>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FinancesPage;
